import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { FileModal, PrepareModal } from '../components/ComplianceModals';
import { useCompany } from '../context/CompanyContext';
import { fyLabel, getCurrentFyStartYear } from '../utils/fy';
import api from '../api';
import './Dashboard.css';
import './Calendar.css';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const CATEGORY_LABELS = {
  GST: 'GST Returns',
  TDS: 'TDS / TCS',
  ROC: 'ROC / MCA',
  Income_Tax: 'Income Tax',
  PF: 'PF',
  ESIC: 'ESIC',
};

const PENALTY_METER_MAX = 50000;

// Standard categories already have a full status-driven left border (see
// cal-border-* in Calendar.css) - these dots are only about telling custom
// compliance categories apart from each other and from standard ones.
const CUSTOM_CATEGORY_COLORS = { FEMA: '#8b5cf6', RBI: '#1e3a8a', SEBI: '#0d9488' };
const DEFAULT_CUSTOM_CATEGORY_COLOR = '#6b7280';

function customCategoryColor(category) {
  if (CATEGORY_LABELS[category]) return null;
  return CUSTOM_CATEGORY_COLORS[category] || DEFAULT_CUSTOM_CATEGORY_COLOR;
}

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function daysUntil(dateStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dateStr + 'T00:00:00');
  return Math.round((due - today) / 86400000);
}

function inr(amount) {
  return `₹${Number(amount).toLocaleString('en-IN')}`;
}

function classifyItem(item) {
  if (item.is_applicable === false) return 'not_applicable';
  if (item.status === 'filed') return 'filed';
  if (item.status === 'in_progress') return 'in_progress';
  if (item.status === 'overdue') return 'overdue';
  const days = daysUntil(item.due_date);
  if (days <= 7) return 'urgent';
  if (days <= 30) return 'soon';
  return 'future';
}

const SORT_RANK = { overdue: 0, urgent: 1, soon: 2, in_progress: 3, future: 4, filed: 5, not_applicable: 6 };

function Calendar() {
  const { selectedCompanyId, fyStartYear, setFyStartYear } = useCompany();
  const [searchParams, setSearchParams] = useSearchParams();
  const today = new Date();

  // If the user hasn't traveled to a different FY via the header selector,
  // default to today's month. Otherwise land on April of the selected FY.
  const isRealCurrentFy = fyStartYear === getCurrentFyStartYear();
  const defaultMonth = isRealCurrentFy ? today.getMonth() + 1 : 4;
  const defaultYear = isRealCurrentFy ? today.getFullYear() : fyStartYear;

  const month = Number(searchParams.get('month')) || defaultMonth;
  const year = Number(searchParams.get('year')) || defaultYear;

  const [items, setItems] = useState([]);
  const [auditItems, setAuditItems] = useState([]);
  const [penaltyExposure, setPenaltyExposure] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalItem, setModalItem] = useState(null);
  const [filing, setFiling] = useState(false);
  const [prepareItem, setPrepareItem] = useState(null);
  const [startingProgress, setStartingProgress] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!searchParams.get('month') || !searchParams.get('year')) {
      setSearchParams({ month: String(month), year: String(year) }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedCompanyId) return;
    api
      .get(`/dashboard/${selectedCompanyId}`, { params: { fy_year: fyStartYear } })
      .then((res) => setPenaltyExposure(res.data.penalty_exposure))
      .catch((err) => setError(err.message));
  }, [selectedCompanyId, fyStartYear]);

  // Keep the header's FY label in sync when the user navigates months across
  // an April boundary (e.g. Mar 2026 -> Apr 2026 moves into FY 2026-27).
  useEffect(() => {
    const derivedFy = month >= 4 ? year : year - 1;
    if (derivedFy !== fyStartYear) {
      setFyStartYear(derivedFy);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year]);

  const loadCalendarItems = () => {
    if (!selectedCompanyId) return;
    setLoading(true);
    api
      .get(`/calendar/${selectedCompanyId}`, { params: { month, year } })
      .then((res) => {
        setItems(res.data);
        setError(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadCalendarItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId, month, year]);

  useEffect(() => {
    if (!selectedCompanyId) return;
    api
      .get(`/audit-calendar/${selectedCompanyId}`, { params: { month, year } })
      .then((res) => setAuditItems(res.data))
      .catch(() => setAuditItems([]));
  }, [selectedCompanyId, month, year]);

  const refreshPenalty = () => {
    if (!selectedCompanyId) return;
    api
      .get(`/dashboard/${selectedCompanyId}`, { params: { fy_year: fyStartYear } })
      .then((res) => setPenaltyExposure(res.data.penalty_exposure));
  };

  const changeMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m > 12) {
      m = 1;
      y += 1;
    } else if (m < 1) {
      m = 12;
      y -= 1;
    }
    setSearchParams({ month: String(m), year: String(y) });
  };

  const openFileModal = (item) => {
    setModalItem(item);
  };

  const closeModal = () => {
    setModalItem(null);
    setFiling(false);
  };

  const confirmFile = (payload) => {
    if (!modalItem) return undefined;
    setFiling(true);
    return api
      .post(`/compliance/${modalItem.id}/file`, payload)
      .then(() => {
        setToast(`${modalItem.form_name} marked as filed ✓`);
        setModalItem(null);
        loadCalendarItems();
        refreshPenalty();
        setTimeout(() => setToast(null), 3500);
      })
      .finally(() => setFiling(false));
  };

  const clearFile = () => {
    if (!modalItem) return undefined;
    setFiling(true);
    return api
      .post(`/compliance/${modalItem.id}/unfile`, {})
      .then(() => {
        setToast(`${modalItem.form_name} marked as unfiled`);
        setModalItem(null);
        loadCalendarItems();
        refreshPenalty();
        setTimeout(() => setToast(null), 3500);
      })
      .finally(() => setFiling(false));
  };

  const confirmStartProgress = () => {
    if (!prepareItem) return;
    setStartingProgress(true);
    api
      .post(`/compliance/${prepareItem.id}/start`, {})
      .then(() => {
        setToast(`${prepareItem.form_name} marked as in progress`);
        setPrepareItem(null);
        loadCalendarItems();
        setTimeout(() => setToast(null), 3500);
      })
      .catch((err) => setError(err.message))
      .finally(() => setStartingProgress(false));
  };

  const meterPct = Math.min(100, Math.round((penaltyExposure / PENALTY_METER_MAX) * 100));

  const sortedItems = [...items].sort((a, b) => {
    const rankDiff = SORT_RANK[classifyItem(a)] - SORT_RANK[classifyItem(b)];
    if (rankDiff !== 0) return rankDiff;
    return a.due_date < b.due_date ? -1 : 1;
  });

  return (
    <PageLayout>
      {error && <p className="dash-error">Backend unreachable: {error}</p>}

      <div className="cal-title-row">
        <div>
          <h1 className="cal-title">Compliance Calendar</h1>
          <p className="cal-subtitle">All statutory due dates — {fyLabel(fyStartYear)}</p>
        </div>

        <div className="cal-month-nav">
          <button className="cal-nav-btn" type="button" onClick={() => changeMonth(-1)}>
            &larr;
          </button>
          <span className="cal-month-label">
            {MONTH_NAMES[month - 1]} {year}
          </span>
          <button className="cal-nav-btn" type="button" onClick={() => changeMonth(1)}>
            &rarr;
          </button>
        </div>
      </div>

      <div className={loading ? 'dash-refreshing' : ''}>
      <div className="dash-card cal-meter-card">
        <div className="cal-meter-top">
          <div>
            <h2 className="cal-meter-title">Live Penalty Exposure Meter</h2>
            <p className="cal-meter-sub">Current exposure if overdue items not filed immediately</p>
          </div>
          <div className="cal-meter-amount dash-serif">{inr(penaltyExposure)}</div>
        </div>

        <div className="cal-meter-bar-wrap">
          <div className="cal-meter-bar">
            <div className="cal-meter-zone green" />
            <div className="cal-meter-zone amber" />
            <div className="cal-meter-zone red" />
            <div className="cal-meter-needle" style={{ left: `${meterPct}%` }} />
          </div>
          <div className="cal-meter-labels">
            <span>₹0 Safe</span>
            <span>₹50,000+ Critical</span>
          </div>
        </div>
      </div>

      <p className="cal-section-label">Upcoming deadlines</p>

      <div className="cal-deadline-list">
        {sortedItems.length === 0 && (
          <div className="dash-card cal-empty">No compliance items for this month.</div>
        )}

        {sortedItems.map((item) => {
          const bucket = classifyItem(item);
          const days = daysUntil(item.due_date);

          return (
            <div
              key={item.id}
              className={`cal-deadline-row cal-border-${bucket} ${bucket === 'not_applicable' ? 'cal-deadline-row-na' : ''}`}
            >
              <div className="cal-deadline-left">
                <div className="cal-deadline-date">{formatDate(item.due_date)}</div>
                <div className="cal-deadline-form">{item.form_name}</div>
                <div className="cal-deadline-category">
                  {customCategoryColor(item.category) && (
                    <span
                      style={{
                        display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                        backgroundColor: customCategoryColor(item.category), marginRight: 6,
                      }}
                    />
                  )}
                  {CATEGORY_LABELS[item.category] || item.category}
                </div>
              </div>

              <div className="cal-deadline-right">
                {bucket === 'not_applicable' && <span className="dash-pill dash-pill-blue">N/A</span>}
                {bucket === 'overdue' && <span className="dash-pill dash-pill-red">Overdue</span>}
                {(bucket === 'urgent' || bucket === 'soon') && (
                  <span className="dash-pill dash-pill-amber">
                    {days <= 0 ? 'Due today' : `${days} days left`}
                  </span>
                )}
                {bucket === 'future' && <span className="dash-pill dash-pill-blue">{days} days</span>}
                {bucket === 'in_progress' && (
                  <span className="dash-pill dash-pill-inprogress">In Progress</span>
                )}
                {bucket === 'filed' && (
                  <span className="dash-pill dash-pill-green">Filed ✓ {formatDate(item.date_of_filing)}</span>
                )}

                {bucket === 'overdue' && (
                  <button
                    className="dash-btn dash-btn-orange"
                    type="button"
                    onClick={() => openFileModal(item)}
                  >
                    File Now
                  </button>
                )}
                {(bucket === 'urgent' || bucket === 'soon' || bucket === 'future') && (
                  <button
                    className="dash-btn cal-btn-grey"
                    type="button"
                    onClick={() => setPrepareItem(item)}
                  >
                    Prepare
                  </button>
                )}
                {bucket === 'in_progress' && (
                  <button
                    className="dash-btn dash-btn-orange"
                    type="button"
                    onClick={() => openFileModal(item)}
                  >
                    File Now
                  </button>
                )}
                {bucket === 'filed' && (
                  <button
                    className="dash-btn dash-btn-green"
                    type="button"
                    onClick={() => openFileModal(item)}
                  >
                    Edit
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {sortedItems.some((item) => customCategoryColor(item.category)) && (
        <div className="cal-legend" style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', margin: '4px 0 16px', fontSize: 13, color: '#6b7280' }}>
          <span>Compliance categories:</span>
          {Object.entries(CUSTOM_CATEGORY_COLORS).map(([cat, color]) => (
            <span key={cat} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: color }} />
              {cat}
            </span>
          ))}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: DEFAULT_CUSTOM_CATEGORY_COLOR }} />
            Other custom
          </span>
        </div>
      )}

      {auditItems.length > 0 && (
        <>
          <p className="cal-section-label">Audit items this month</p>
          <div className="dash-card">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Due Date</th>
                  <th>Status</th>
                  <th>Risk</th>
                </tr>
              </thead>
              <tbody>
                {auditItems.map((item) => (
                  <tr key={`${item.kind}-${item.id}`}>
                    <td>
                      <Link to="/audit-status">{item.title}</Link>
                      <div className="dash-table-sub">{item.kind === 'occurrence' ? 'Audit' : 'Recommendation'}</div>
                    </td>
                    <td>{formatDate(item.due_date)}</td>
                    <td>
                      <span className="dash-pill dash-pill-blue">{item.status}</span>
                    </td>
                    <td>
                      {item.risk_rating && (
                        <span className={`dash-pill ${item.risk_rating === 'High' ? 'dash-pill-red' : item.risk_rating === 'Med' ? 'dash-pill-amber' : 'dash-pill-blue'}`}>
                          {item.risk_rating}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      </div>

      <FileModal item={modalItem} onClose={closeModal} onConfirm={confirmFile} onClear={clearFile} busy={filing} />

      <PrepareModal
        item={prepareItem}
        onClose={() => setPrepareItem(null)}
        onMarkInProgress={confirmStartProgress}
        busy={startingProgress}
      />

      {toast && <div className="cal-toast">{toast}</div>}
    </PageLayout>
  );
}

export default Calendar;
